import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Card,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  CardContent,
  Typography
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { getJobOrders } from "../API/jobOrderAPI";
import { getCacheClients } from "../API/clientAPI";
import { fetchAndFilterJobOrders } from "./utils/JobOrderFilter";
import { styled } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";
import BaseWrapper from "../../components/shared/BaseWrapper";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ButtonGroup from '@mui/material/ButtonGroup';

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  opacity: 0.8,
}));

const getTodayInPH = () => {
    const now = new Date();
    const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0
    );
    localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
    return localDate.toISOString().split("T")[0];
  };

function ManageJobOrders() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [clientsCache, setClientsCache] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [isDateRange, setIsDateRange] = useState(false);
  // const [searchFilter, setSearchFilter] = useState({
  //   client: "",
  //   startDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth(),
  //       1, // First day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth() + 1,
  //       0, // Last day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endorsementForm: "",
  // });

  const [searchFilter, setSearchFilter] = useState({
    client: "",
    startDate: getTodayInPH(),
    endDate: getTodayInPH(),
    endorsementForm: "",
  });

  const handleSingleDateChange = (value) => {
    setSearchFilter((prev) => ({
      ...prev,
      startDate: value,
      endDate: value,
    }));
  };

  const handleDateRangeToggle = () => {
    setIsDateRange((prev) => {
      const next = !prev;

      // When switching back to single-date mode,
      // force endDate to match startDate so search still works.
      if (!next) {
        setSearchFilter((current) => ({
          ...current,
          endDate: current.startDate,
        }));
      }

      return next;
    });
  };

  const navigate = useNavigate();

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        setClientsLoading(true);
        // Fetch clients
        const clientsCache = await getCacheClients(); // Pass status if needed

        setClientsCache(clientsCache);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchCacheClients();
  }, []);

  const formatTime = (time) => {
    if (!time) return "";
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${String(minute).padStart(2, "0")} ${period}`;
  };

  const handleSearchButtonClick = async (selectedStatusArg) => {
    try {
      setIsLoading(true);
      
      const effectiveStatus = typeof selectedStatusArg === "string"
        ? selectedStatusArg
        : statusFilter;

      const statusParam = effectiveStatus === "All" ? [] : [effectiveStatus];

      // Call the shared util you renamed
      const { list } = await fetchAndFilterJobOrders({
        searchFilter,                // expects { client, startDate, endDate, endorsementForm }
        status: statusParam,         // array (e.g., [], ["Done"], ["Not Done"])
        serviceReportStatus: [],     // no SR filter on Manage Job Orders
        billingRecordStatus: [],
        userRoles: [],               // add if you scope by role on this screen
        technicianId: null,          // add if you scope by tech here
        getJobOrdersApi: getJobOrders,
      });

      // View-specific shaping for this screen
      const shaped = list.map((job) => ({
        id: job.id,
        job_date: job.job_date,
        start_time: formatTime(job.start_time),
        end_time: formatTime(job.end_time),
        technicians: Array.isArray(job.technicians) ? job.technicians : [],
        job_types: Array.isArray(job.job_types) ? job.job_types : [],
        client: job.client || "",
        status: job.status || "",
      }));

      setJobOrders(shaped);
      setError(null);
    } catch (err) {
      setError(err.message || String(err));
      setErrorMessage("Failed to fetch Job Orders. Please try again.");
    } finally {
      setIsLoading(false);
    }  
  }

  const handleSearchClient = (event, value) => {
    setIsFormVisible(false);
    setSearchFilter((prev) => ({
      ...prev,
      client: value,
    }));
  };

  const handleStatusChange  = async (e) => {
    const next = e.target.value;
    setStatusFilter(next);
    // Re-run search with the new selection so results are filtered server-side
    await handleSearchButtonClick(next);
  };

  // status → colored dot (green, blue, grey, red)
  const statusDotSx = (status) => (theme) => {
    const key = (status || '').toLowerCase();
    const colorMap = {
      'done': theme.palette.success.main,              // green
      'not done': theme.palette.info.main,             // blue
      'rescheduled': theme.palette.grey[500],          // grey
      'cancelled by client': theme.palette.error.main, // red
    };
    return {
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: colorMap[key] || theme.palette.text.disabled,
      flex: '0 0 8px',
    };
  };


  return (
    <BaseWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Manage Job Orders"
      subTitleRoute="/manage-job-orders"
      createButtonLabel="Create Job Order"
      onClickCreateButton={() => navigate("/create-job-order")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid
        container
        spacing={2}
        alignItems="stretch"
        sx={{
          "& .MuiFormControl-root": {
            height: "100%",
          },
          "& .MuiOutlinedInput-root": {
            minHeight: 40,
          },
          "& .MuiButton-root.filter-action-btn": {
            minHeight: 40,
          },
        }}
      >
        <Grid item xs={12} md={6} lg={4}>
          <Autocomplete
            options={clientsCache}
            getOptionLabel={(client) => client?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(options, params) => clientFilter(options, params)}
            openOnFocus
            autoHighlight
            loading={clientsLoading}
            noOptionsText={clientsLoading ? "Loading..." : "No clients found"}
            value={searchFilter.client || null}
            onChange={(event, newValue) => handleSearchClient(event, newValue)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>{option.name}</span>
                  <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client Name"
                variant="outlined"
                fullWidth
                error={!!error}
                helperText={error || ""}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-end",
              height: "100%",
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {!isDateRange ? (
              <TextField
                label="Job Date"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={searchFilter.startDate}
                onChange={(e) => handleSingleDateChange(e.target.value)}
              />
            ) : (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={searchFilter.startDate}
                  onChange={(e) =>
                    setSearchFilter((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />

                <TextField
                  label="End Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={searchFilter.endDate}
                  inputProps={{
                    min: searchFilter.startDate || undefined,
                  }}
                  onChange={(e) =>
                    setSearchFilter((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
              </>
            )}

            <Button
              className="filter-action-btn"
              variant={isDateRange ? "contained" : "outlined"}
              onClick={handleDateRangeToggle}
              sx={{
                whiteSpace: "nowrap",
                minWidth: { xs: "100%", sm: 140 },
                flexShrink: 0,
              }}
            >
              {isDateRange ? "Single Date" : "Date Range"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="endorsement-form-label">Endorsement Form</InputLabel>
            <Select
              labelId="endorsement-form-label"
              value={searchFilter.endorsementForm}
              onChange={(e) =>
                setSearchFilter((prev) => ({
                  ...prev,
                  endorsementForm: e.target.value,
                }))
              }
              label="Endorsement Form"
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
          <Button
            className="filter-action-btn"
            variant="contained"
            color="primary"
            onClick={handleSearchButtonClick}
            disabled={isLoading}
            fullWidth
            sx={{
              height: "100%",
            }}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      <StyledHr />
      {/* Total Job Orders Card */}
      <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
        <Grid item xs={2}>
          <Card
            sx={{
              backgroundColor: '#1976D2',
              color: '#FFF',
              borderRadius: 1,
              textAlign: 'center',
              padding: '4px',
              boxShadow: 1
            }}
          >
            <CardContent sx={{ padding: '4px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                Total Job Orders
              </Typography>
              <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                {jobOrders.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ padding: "20px" }}>
        <TableContainer >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell ><b>Job Date</b></TableCell>
                <TableCell ><b>Start Time</b></TableCell>
                <TableCell ><b>End Time</b></TableCell>
                <TableCell ><b>Client</b></TableCell>
                <TableCell ><b>Job Type</b></TableCell>
                <TableCell ><b>Technicians</b></TableCell>
                {/* Service Report Status (Header Dropdown) */}
                <TableCell sx={{ width: 200, pt: 1, pb: 1}}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-label">Job Order Status</InputLabel>
                    <Select
                      labelId="status-label"
                      label="Job Order Status"
                      value={statusFilter}
                      onChange={handleStatusChange}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Not Done">Not Done</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                      <MenuItem value="Cancelled By Client">Cancelled By Client</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell ><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(jobOrders) && jobOrders.length > 0 ? (
                jobOrders.map((job, index) => (
                  <TableRow key={index}>
                    <TableCell >{job.job_date || ""}</TableCell>
                    <TableCell >{job.start_time || ""}</TableCell>
                    <TableCell >{job.end_time || ""}</TableCell>
                    <TableCell >{job.client || ""}</TableCell>                    
                    <TableCell
                      sx={{
                        // Limit the width so it doesn't push other columns.
                        maxWidth: 400,
                        // Enforce wrapping if Chips take multiple lines.
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {Array.isArray(job.job_types) && job.job_types.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,  // space between chips
                          }}
                        >
                          {job.job_types.map((type, index) => (
                            <Chip
                              key={index}
                              label={type.replace(/_/g, " ")}
                              variant="outlined"
                              size="small"                              
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell> 
                    <TableCell >
                      {job.technicians.length > 0
                        ? job.technicians.map((tech, i) => (
                          <div key={i}>{`${tech.firstname} ${tech.lastname}`}</div>
                        ))
                        : ""}
                    </TableCell>
                    {/* Job Order Status cell */}
                    <TableCell>
                      {job.status ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={statusDotSx(job.status)} />
                          <span>{job.status}</span>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell >
                      <ButtonGroup
                        variant="outlined"
                        size="small"
                        aria-label="action button group"
                        sx={{
                          gap: 0, // Ensure no gap between buttons
                          padding: 0, // Optional: if you want to remove extra space around the group
                        }}
                      >
                        <Tooltip title="View Job Order" arrow>
                          <Button
                            // onClick={() => navigate(`/view-job-order/${job.id}`)}
                            onClick={() => window.open(`/view-job-order/${job.id}`, '_blank')}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              textTransform: "none",
                              minWidth: "32px", // Ensure buttons are compact
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>

                        <Tooltip title="Edit Job Order" arrow>
                          <Button
                            // onClick={() => navigate(`/edit-job-order/${job.id}`)}
                            onClick={() => window.open(`/edit-job-order/${job.id}`, '_blank')}
                            startIcon={<EditIcon />}
                            sx={{
                              textTransform: "none",
                              minWidth: "32px", // Ensure buttons are compact
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>

                        <Tooltip title="Delete Job Order" arrow>
                          <Button
                            // onClick={() => navigate(`/delete-job-order/${job.id}`)}
                            onClick={() => window.open(`/delete-job-order/${job.id}`, '_blank')}
                            startIcon={<DeleteIcon />}
                            color="error"
                            sx={{
                              textTransform: "none",
                              minWidth: "32px", // Ensure buttons are compact
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  {/* 8 = total number of columns */}
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    {isLoading ? "Loading..." : "No job orders found for this filter and date range."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Card>
      {/* )} */}
    </BaseWrapper>
  );
}

export default ManageJobOrders;
